import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Trash2, Loader2, X, Save, TriangleAlert, Truck, Route, Waypoints } from 'lucide-react';
import { useAppContext } from '../../../context/AppContext';
import { interDepotService } from '../../../services/interDepotService';
import SearchSelect from '../../../components/SearchSelect';
import PostingSummary from '../../../components/PostingSummary';
import MantleDataGrid, { gcell } from '../../../components/MantleDataGrid';
import LaneNetworkMap from '../../../components/LaneNetworkMap';
import { PERIODS, periodLabel } from '../../../utils/financialPeriods';
import usePeriodDrilldown, { periodColumns, periodsOfCol, periodHeaderToggle, aggPeriods } from '../../../hooks/usePeriodDrilldown';

// =============================================================================
// Distribution > Inter-Depot Transport  (RATING)
//
// Planning decides what moves; this page decides what moving it costs. Same
// lanes either way, so a company that never buys the planning module still gets
// a working inter-depot budget — it just types the volumes instead.
//
// A LANE HAS MANY CARRIERS. That is the shape everything else follows from:
//
//     PLNT-01 -> WH-01          one edge, one road, one distance
//       Own fleet     Superlink  60%  per km    R18.50
//       Fast Freight  Superlink  40%  per trip  R9 500
//
// The share is the CARRIER'S share of the lane, not a slice of some volume
// driver. That is what replaced the old mockup's `% Alloc`: once a lane carries
// real volume — from planning or typed here — deriving it from a percentage of
// national outflow is strictly worse.
//
// VOLUME comes from planning or manual entry, switched by the toggle at the
// top. Both are in the same unit (pallets), so the toggle changes where the
// number comes from and never what it means.
//
// FIXED FLEET COST IS NOT HERE. An owned lane carries the variable cost of
// movement only — fuel, tolls, variable maintenance. Lease, insurance and
// licensing live on Warehousing > Fixed Costs. Putting them here as well is the
// easiest double-count in the module.
// =============================================================================

const RATE_BASES = [
  { code: 'PER_TRIP', label: 'Per trip', hint: 'rate × trips' },
  { code: 'PER_KM', label: 'Per km', hint: 'rate × trips × round-trip km' },
  { code: 'PER_UNIT', label: 'Per unit', hint: 'rate × volume' },
];
const fmtR = (n) => `R ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtN = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const sum = (a) => (a || []).reduce((x, v) => x + (Number(v) || 0), 0);

// Grid cell themes. Derived and total rows are shaded so a computed number
// never looks like something you can type over.
const T_READONLY = { bgCell: 'rgba(100, 116, 139, 0.08)', textDark: '#64748b' };
const T_LABEL = { bgCell: 'rgba(100, 116, 139, 0.08)', textDark: '#334155', baseFontStyle: '600 12px' };
const T_TOTAL = { bgCell: 'rgba(100, 116, 139, 0.12)', baseFontStyle: '600 12px' };
const T_SUBTOTAL = { bgCell: 'rgba(100, 116, 139, 0.05)', textDark: '#64748b' };

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors';
const field = 'text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2 py-1.5 outline-none focus:ring-2 focus:ring-blue-500/40 disabled:opacity-40';
const numField = `${field} text-right`;

export default function InterDepot() {
  const { selectedVersion, selectedSite, fyStartMonth } = useAppContext();

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const [lanes, setLanes] = useState([]);
  const [rates, setRates] = useState([]);
  const [manualVolumes, setManualVolumes] = useState({});
  const [planningVolumes, setPlanningVolumes] = useState({});
  const [picks, setPicks] = useState({ contractors: [], vehicleTypes: [], accounts: [], costCenters: [], sites: [], priceIndices: [] });
  const [capacityUom, setCapacityUom] = useState('PALLET');
  const [volumeSource, setVolumeSourceState] = useState('PLANNING');
  const [shareDefaults, setShareDefaults] = useState({ PER_KM: 40, PER_TRIP: 35, PER_UNIT: 10 });

  // The rate card opens first; the map is a second way in. Deliberately that
  // order — this is a rating page, and somebody arriving to change a rate
  // should not have to find it on a graph.
  const [view, setView] = useState('rates');   // 'rates' | 'network'

  const [selectedLaneId, setSelectedLaneId] = useState(null);
  const [dirtyRates, setDirtyRates] = useState({});   // { rate_line_id: true }
  const [dirtyVolume, setDirtyVolume] = useState(false);
  const [addingLane, setAddingLane] = useState(false);
  const [newLane, setNewLane] = useState({ origin_site_code: '', dest_site_code: '', distance_km: '', return_factor: '2' });
  // What the monthly grid shows. All three series come back per period from
  // vw_bud_transfer_cost, so this costs nothing but a switch.
  const [costMetric, setCostMetric] = useState('amount');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await interDepotService.getLanes();
      const d = res.data || {};
      setLanes(d.lanes || []);
      setRates((d.rates || []).map((r) => ({ ...r, rates: [...r.rates] })));
      setManualVolumes(d.manualVolumes || {});
      setPlanningVolumes(d.planningVolumes || {});
      setPicks({
        contractors: d.contractors || [], vehicleTypes: d.vehicleTypes || [],
        accounts: d.accounts || [], costCenters: d.costCenters || [], sites: d.sites || [],
        priceIndices: d.priceIndices || [],
      });
      setCapacityUom(d.capacityUom || 'PALLET');
      setVolumeSourceState(d.config?.volume_source || 'PLANNING');
      if (d.config?.fuel_share_defaults) setShareDefaults(d.config.fuel_share_defaults);
      setDirtyRates({});
      setDirtyVolume(false);
      setSelectedLaneId((cur) => (cur && (d.lanes || []).some((l) => l.lane_id === cur) ? cur : (d.lanes || [])[0]?.lane_id || null));
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load inter-depot lanes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (selectedVersion) load(); }, [load, selectedVersion, selectedSite]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const lane = lanes.find((l) => l.lane_id === selectedLaneId) || null;
  const laneRates = useMemo(() => rates.filter((r) => r.lane_id === selectedLaneId), [rates, selectedLaneId]);

  // Volume actually in force for this lane, per its own source.
  const laneVolume = useCallback((l) => {
    if (!l) return Array(12).fill(0);
    return (l.volume_source === 'MANUAL' ? manualVolumes[l.lane_id] : planningVolumes[l.lane_id]) || Array(12).fill(0);
  }, [manualVolumes, planningVolumes]);

  const activeVolume = laneVolume(lane);
  const allocTotal = laneRates.reduce((a, r) => a + Number(r.allocation_pct || 0), 0);
  const laneCost = laneRates.reduce((a, r) => a + sum(r.cost?.amount), 0);
  const grandTotal = rates.reduce((a, r) => a + sum(r.cost?.amount), 0);

  // Lanes the toggle says should come from planning but that planning does not
  // cover. Without this they read as a flat zero and nobody notices.
  const unplanned = useMemo(
    () => lanes.filter((l) => l.volume_source === 'PLANNING' && sum(planningVolumes[l.lane_id]) === 0),
    [lanes, planningVolumes]
  );

  // ---- Actions -------------------------------------------------------------
  const run = async (fn, okMsg) => {
    setBusy(true);
    setError('');
    try {
      const res = await fn();
      await load();
      setToast(okMsg || res?.message || 'Saved.');
      return true;
    } catch (e) {
      setError(e.response?.data?.message || e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const toggleSource = (value) => run(
    () => interDepotService.setVolumeSource(value),
    `Volume now comes from ${value === 'PLANNING' ? 'the planning module' : 'manual entry'}.`
  );

  const addLane = async () => {
    if (!newLane.dest_site_code) { setError('Choose a destination site.'); return; }
    const ok = await run(() => interDepotService.addLane({
      origin_site_code: newLane.origin_site_code || undefined,
      dest_site_code: newLane.dest_site_code,
      distance_km: Number(newLane.distance_km) || 0,
      return_factor: Number(newLane.return_factor) || 2,
      volume_source: 'MANUAL',
    }), 'Lane added.');
    if (ok) { setAddingLane(false); setNewLane({ origin_site_code: '', dest_site_code: '', distance_km: '', return_factor: '2' }); }
  };

  const patchLane = (patch) => run(() => interDepotService.saveLane({ lane_id: selectedLaneId, ...patch }));

  const addCarrier = (carrierType) => run(
    () => interDepotService.addRateLine({ lane_id: selectedLaneId, carrier_type: carrierType,
      contractor_code: carrierType === 'CONTRACTED' ? picks.contractors[0]?.contractor_code : undefined }),
    'Carrier added.'
  );

  const setRate = (id, patch) => {
    setRates((prev) => prev.map((r) => (r.rate_line_id === id ? { ...r, ...patch } : r)));
    setDirtyRates((d) => ({ ...d, [id]: true }));
  };

  // ---- Price-index linking --------------------------------------------------
  // Linking stamps the base from the index and the share from policy, both
  // overridable on the line. The base is COPIED, not read live: two carriers
  // quoted six months apart must not escalate off one base, or the later one is
  // charged for a rise already inside its price.
  const indexByCode = useMemo(
    () => new Map((picks.priceIndices || []).map((i) => [i.index_code, i])),
    [picks.priceIndices]
  );

  const linkRate = (r, code) => {
    if (!code) { setRate(r.rate_line_id, { index_code: null, index_base_value: null, index_share_pct: 0 }); return; }
    const idx = indexByCode.get(code);
    setRate(r.rate_line_id, {
      index_code: code,
      index_base_value: Number(idx?.default_base_value) || 0,
      index_share_pct: Number(shareDefaults[r.rate_basis] ?? 0),
    });
  };

  // 1 + share x (now/base - 1), measured against the year average because this
  // cell edits one annual rate. The cost view does it month by month.
  const factorOf = (r) => {
    const idx = indexByCode.get(r.index_code);
    const base = Number(r.index_base_value) || 0;
    const now = Number(idx?.avg_value) || 0;
    const share = Number(r.index_share_pct) || 0;
    if (!r.index_code || base <= 0 || now <= 0 || share === 0) return 1;
    return 1 + (share / 100) * ((now / base) - 1);
  };

  // The carrier's expected annual increase, as this row will feel it ON AVERAGE
  // over the year — a step in July is only two-thirds of a 6% rise across
  // twelve periods, and quoting the full 6% next to the rate would overstate
  // what it costs. The cost view applies the step properly, month by month.
  const increaseFactorOf = (r) => {
    const pct = Number(r.contractor_increase_pct) || 0;
    const from = Number(r.contractor_increase_period) || 0;
    if (r.carrier_type === 'OWNED' || r.apply_contractor_increase === false) return 1;
    if (!pct || !from || from > 12) return 1;
    const months = 12 - from + 1;
    return 1 + (pct / 100) * (months / 12);
  };
  // The two multiply where the rate is drawn: diesel first, then the escalation
  // on top of the fuel-adjusted rate — the same order as the cost view, which
  // is the order an invoice works in.

  const setRateValue = (id, period, value) => {
    setRates((prev) => prev.map((r) => {
      if (r.rate_line_id !== id) return r;
      const next = [...r.rates];
      if (r.rate_mode === 'ANNUAL') next.fill(Number(value) || 0);
      else next[period - 1] = Number(value) || 0;
      return { ...r, rates: next };
    }));
    setDirtyRates((d) => ({ ...d, [id]: true }));
  };

  // Typing into one month means the year is no longer a single number, so the
  // line flips to MONTHLY on the spot. Making the user find a mode toggle first
  // is the kind of step that gets skipped, and then the edit silently vanishes
  // on save because ANNUAL spreads period 1 across all twelve.
  const editRate = (id, period, value) => {
    setRates((prev) => prev.map((r) => {
      if (r.rate_line_id !== id) return r;
      const next = [...r.rates];
      next[period - 1] = Number(value) || 0;
      return { ...r, rates: next, rate_mode: 'MONTHLY' };
    }));
    setDirtyRates((d) => ({ ...d, [id]: true }));
  };

  // "5% from July" — the actual shape of a tariff increase. Multiplies the
  // rates already there from the chosen month on, so it works off whatever base
  // each carrier has rather than assuming they all start equal.
  const applyEscalation = (pct, fromPeriod) => {
    const factor = 1 + (Number(pct) || 0) / 100;
    if (factor === 1) return;
    const touched = {};
    setRates((prev) => prev.map((r) => {
      if (r.lane_id !== selectedLaneId) return r;
      const next = [...r.rates];
      // ANNUAL lines hold one figure in all twelve slots; escalating part of the
      // year is what makes them monthly.
      for (let i = fromPeriod - 1; i < 12; i += 1) next[i] = Math.round(next[i] * factor * 100) / 100;
      touched[r.rate_line_id] = true;
      return { ...r, rates: next, rate_mode: 'MONTHLY' };
    }));
    setDirtyRates((d) => ({ ...d, ...touched }));
  };

  const saveRates = () => {
    const lines = rates.filter((r) => dirtyRates[r.rate_line_id]).map((r) => ({
      rate_line_id: r.rate_line_id, carrier_type: r.carrier_type, contractor_code: r.contractor_code,
      vehicle_type_code: r.vehicle_type_code, allocation_pct: r.allocation_pct, rate_basis: r.rate_basis,
      rate_mode: r.rate_mode, rates: r.rates,
      index_code: r.index_code || null,
      index_base_value: r.index_code ? Number(r.index_base_value) || 0 : null,
      index_share_pct: r.index_code ? Number(r.index_share_pct) || 0 : 0,
    }));
    if (!lines.length) return Promise.resolve();
    return run(() => interDepotService.saveRateLines(lines), 'Carrier rates saved.');
  };

  const setManualPeriod = (period, value) => {
    setManualVolumes((prev) => {
      const cur = [...(prev[selectedLaneId] || Array(12).fill(0))];
      cur[period - 1] = Number(value) || 0;
      return { ...prev, [selectedLaneId]: cur };
    });
    setDirtyVolume(true);
  };

  const saveVolume = () => run(
    () => interDepotService.saveManualVolumes(selectedLaneId, manualVolumes[selectedLaneId] || Array(12).fill(0)),
    'Volumes saved.'
  );

  // ---- Network view ----------------------------------------------------------
  // Nodes are sites, edges are lanes — the same graph Planning draws, asking the
  // rating question instead: is this lane priced, and does its carrier split
  // actually add up.
  const netNodes = useMemo(() => {
    const seen = new Map();
    const touch = (code, tier) => {
      if (!seen.has(code)) {
        const s = picks.sites.find((x) => x.site_code === code);
        seen.set(code, { id: code, label: s?.site_name || code, sublabel: code, tier, out: 0, cost: 0 });
      }
      return seen.get(code);
    };
    lanes.forEach((l) => {
      const o = touch(l.origin_site_code, 0);
      touch(l.dest_site_code, 1);
      o.out += 1;
      o.cost += rates.filter((r) => r.lane_id === l.lane_id).reduce((a, r) => a + sum(r.cost?.amount), 0);
    });
    return [...seen.values()].map((n) => ({
      ...n,
      colour: n.out > 0 ? '#0284c7' : '#64748b',
      metric: n.out > 0 ? `Dispatches ${n.out} · ${fmtR(n.cost)}` : 'Receives only',
    }));
  }, [lanes, rates, picks.sites]);

  const netEdges = useMemo(() => lanes.map((l) => {
    const lr = rates.filter((r) => r.lane_id === l.lane_id);
    const cost = lr.reduce((a, r) => a + sum(r.cost?.amount), 0);
    const share = lr.reduce((a, r) => a + (Number(r.allocation_pct) || 0), 0);
    // The two states worth spotting from across the room: a lane nobody carries,
    // and a lane whose carriers do not add to 100 — under- or over-costed by
    // exactly the gap.
    const tone = lr.length === 0 ? 'empty' : Math.round(share) !== 100 ? 'bad' : 'priced';
    return {
      id: l.lane_id,
      from: l.origin_site_code,
      to: l.dest_site_code,
      label: lr.length === 0 ? 'no carrier' : fmtR(cost),
      tone,
      lane: l,
      carriers: lr.length,
      share,
    };
  }), [lanes, rates]);

  const badLaneCount = useMemo(
    () => netEdges.filter((e) => e.tone !== 'priced').length,
    [netEdges]
  );

  if (!selectedVersion) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">Select a budget version to budget inter-depot transport.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Inter-Depot Transport</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Pick a lane, then add a carrier per leg — own fleet, a contractor, or both split by share.
              {selectedSite ? <> Showing lanes leaving <strong>{selectedSite}</strong>.</> : ' Showing every site.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Same page, two ways in. The rate card is the default because it
                is where the work is; the map answers "which lane have we not
                priced" faster than any list can. */}
            <div className="flex items-center rounded-md border border-slate-300 dark:border-slate-600 overflow-hidden">
              {[['rates', 'Lanes & Rates'], ['network', 'Network']].map(([v, label]) => (
                <button key={v} onClick={() => setView(v)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors ${view === v
                    ? 'bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900'
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
                  {v === 'network' && <Waypoints className="w-3.5 h-3.5" />}{label}
                  {v === 'network' && badLaneCount > 0 && (
                    <span className="ml-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300">
                      {badLaneCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div className="flex items-center rounded-md border border-slate-300 dark:border-slate-600 overflow-hidden">
              {['PLANNING', 'MANUAL'].map((v) => (
                <button key={v} onClick={() => toggleSource(v)} disabled={busy}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${volumeSource === v
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
                  {v === 'PLANNING' ? 'Planning volumes' : 'Manual volumes'}
                </button>
              ))}
            </div>
            <button className={btn} onClick={() => setAddingLane((a) => !a)} disabled={busy}>
              <Plus className="w-3.5 h-3.5" /> Add lane
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-3 flex items-start justify-between gap-2 px-3 py-2 rounded-lg border border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10">
            <p className="m-0 text-xs text-red-700 dark:text-red-300">{error}</p>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
        {toast && (
          <div className="mt-3 px-3 py-2 rounded-lg border border-emerald-300 bg-emerald-50 dark:border-emerald-500/40 dark:bg-emerald-500/10">
            <p className="m-0 text-xs text-emerald-700 dark:text-emerald-300">{toast}</p>
          </div>
        )}
        {volumeSource === 'PLANNING' && unplanned.length > 0 && (
          <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10">
            <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="m-0 text-xs text-amber-800 dark:text-amber-300">
              <strong>{unplanned.length} lane(s)</strong> have no planning volume and will budget at zero:{' '}
              {unplanned.map((l) => `${l.origin_site_code}→${l.dest_site_code}`).join(', ')}.
              Switch to manual volumes, or plan them in the Planning module.
            </p>
          </div>
        )}

        {addingLane && (
          <div className="mt-3 flex flex-wrap items-end gap-2 px-3 py-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800">
            {!selectedSite && (
              <div className="min-w-[200px]">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">From</label>
                <SearchSelect value={newLane.origin_site_code} onChange={(v) => setNewLane((n) => ({ ...n, origin_site_code: v }))}
                  options={picks.sites.map((s) => ({ code: s.site_code, name: s.site_name }))} placeholder="Origin site…" />
              </div>
            )}
            <div className="min-w-[200px]">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">To</label>
              <SearchSelect value={newLane.dest_site_code} onChange={(v) => setNewLane((n) => ({ ...n, dest_site_code: v }))}
                options={picks.sites.map((s) => ({ code: s.site_code, name: s.site_name }))} placeholder="Destination site…" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Distance (km, one way)</label>
              <input className={numField} style={{ width: 130 }} type="number" min="0" value={newLane.distance_km}
                     onChange={(e) => setNewLane((n) => ({ ...n, distance_km: e.target.value }))} />
            </div>
            <button className={btnPrimary} onClick={addLane} disabled={busy}>Add lane</button>
            <button className={btn} onClick={() => setAddingLane(false)}>Cancel</button>
            <p className="m-0 w-full text-[11px] text-slate-400">
              Lanes added here are the same lanes the Planning module draws — the two teams describe one network.
            </p>
          </div>
        )}
      </div>

      {view === 'network' && (
        <div className="flex-1 min-h-0 px-6 pb-6 flex flex-col">
          <LaneNetworkMap
            nodes={netNodes}
            edges={netEdges}
            selectedEdgeId={selectedLaneId}
            onSelectEdge={(e) => setSelectedLaneId(e ? e.id : null)}
            loading={loading}
            emptyTitle="No lanes to draw yet."
            emptyHint="Add one on Lanes & Rates, or draw the network in the Planning module — they are the same lanes."
            legend={<NetworkLegend />}
            inspector={selectedLaneId ? (
              <NetworkLaneInspector
                edge={netEdges.find((e) => e.id === selectedLaneId)}
                rates={rates.filter((r) => r.lane_id === selectedLaneId)}
                picks={picks}
                busy={busy}
                onEdit={setRate}
                onAddCarrier={addCarrier}
                onRemove={(id) => run(() => interDepotService.deleteRateLine(id), 'Carrier removed.')}
                onSave={saveRates}
                onOpenFull={() => setView('rates')}
                dirty={Object.keys(dirtyRates).length > 0}
              />
            ) : null}
          />
        </div>
      )}

      <div className={`flex-1 min-h-0 px-6 pb-6 gap-4 ${view === 'rates' ? 'flex' : 'hidden'}`}>
        {/* Lane list */}
        <div className="w-72 shrink-0 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-y-auto">
          <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-800">
            <p className="m-0 text-[10px] font-bold uppercase tracking-wider text-slate-400">Lanes ({lanes.length})</p>
          </div>
          {loading ? (
            <div className="h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : lanes.length === 0 ? (
            <p className="m-0 px-3 py-6 text-xs text-slate-400 text-center">
              No lanes yet. Add one, or draw them in the Planning module.
            </p>
          ) : lanes.map((l) => {
            const vol = sum(laneVolume(l));
            const cost = rates.filter((r) => r.lane_id === l.lane_id).reduce((a, r) => a + sum(r.cost?.amount), 0);
            const carriers = rates.filter((r) => r.lane_id === l.lane_id).length;
            const sel = l.lane_id === selectedLaneId;
            return (
              <button key={l.lane_id} onClick={() => setSelectedLaneId(l.lane_id)}
                className={`w-full text-left px-3 py-2.5 border-b border-slate-100 dark:border-slate-700/60 transition-colors ${sel
                  ? 'bg-blue-50 dark:bg-blue-500/10 border-l-2 border-l-blue-500'
                  : 'hover:bg-slate-50 dark:hover:bg-slate-700/40'}`}>
                <div className="flex items-center gap-1.5">
                  <p className="m-0 text-xs font-bold text-slate-800 dark:text-slate-100 truncate">
                    {l.origin_site_code} → {l.dest_site_code}
                  </p>
                  {l.lane_type === 'RETURNS' && (
                    <span className="px-1 py-px text-[9px] font-bold uppercase rounded bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">Ret</span>
                  )}
                </div>
                <p className="m-0 mt-0.5 text-[11px] text-slate-400 truncate">{l.dest_name}</p>
                <div className="mt-1 flex items-center gap-2 text-[10px]">
                  <span className={vol === 0 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500'}>
                    {fmtN(vol)} {capacityUom.toLowerCase()}
                  </span>
                  <span className="text-slate-300">·</span>
                  <span className="text-slate-500">{carriers} carrier{carriers === 1 ? '' : 's'}</span>
                  <span className="ml-auto font-semibold text-slate-600 dark:text-slate-300">{fmtR(cost)}</span>
                </div>
              </button>
            );
          })}
          <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-700 sticky bottom-0 bg-slate-50 dark:bg-slate-900/60">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Total</span>
              <span className="text-sm font-bold text-slate-800 dark:text-slate-100">{fmtR(grandTotal)}</span>
            </div>
          </div>
        </div>

        {/* Lane detail */}
        <div className="flex-1 min-w-0 overflow-y-auto space-y-4">
          {!lane ? (
            <div className="h-full flex flex-col items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
              <Route className="w-8 h-8 text-slate-300 dark:text-slate-600" />
              <p className="m-0 text-sm text-slate-500 dark:text-slate-400">Select a lane to price it.</p>
            </div>
          ) : (
            <>
              {/* Lane header */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <h2 className="m-0 text-base font-bold text-slate-900 dark:text-slate-100">
                      {lane.origin_name} → {lane.dest_name}
                    </h2>
                    <p className="m-0 mt-0.5 text-xs text-slate-400">
                      Volume from {lane.volume_source === 'MANUAL' ? 'manual entry' : 'the planning module'} ·{' '}
                      {fmtN(sum(activeVolume))} {capacityUom.toLowerCase()} / year
                    </p>
                  </div>
                  <div className="flex items-end gap-3">
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Distance (km, one way)</label>
                      <input className={numField} style={{ width: 120 }} type="number" min="0"
                             defaultValue={lane.distance_km} key={`d-${lane.lane_id}`}
                             onBlur={(e) => { const v = Number(e.target.value) || 0; if (v !== lane.distance_km) patchLane({ distance_km: v }); }} />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Return factor</label>
                      <input className={numField} style={{ width: 100 }} type="number" min="1" step="0.1"
                             defaultValue={lane.return_factor} key={`r-${lane.lane_id}`}
                             onBlur={(e) => { const v = Number(e.target.value) || 1; if (v !== lane.return_factor) patchLane({ return_factor: v }); }} />
                    </div>
                    <p className="m-0 pb-2 text-[11px] text-slate-400 max-w-[190px]">
                      Round trip {fmtN(lane.distance_km * lane.return_factor)} km. 2.0 returns empty; 1.0 if backhaul is sold.
                    </p>
                  </div>
                </div>
              </div>

              {/* Volume */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="m-0 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Lane volume ({capacityUom.toLowerCase()})
                  </p>
                  {lane.volume_source === 'MANUAL' && (
                    <button className={btnPrimary} onClick={saveVolume} disabled={busy || !dirtyVolume}>
                      <Save className="w-3.5 h-3.5" /> Save volumes
                    </button>
                  )}
                </div>
                <VolumeGrid
                  lane={lane}
                  manual={manualVolumes[lane.lane_id] || Array(12).fill(0)}
                  planning={planningVolumes[lane.lane_id] || Array(12).fill(0)}
                  fyStartMonth={fyStartMonth}
                  onEdit={setManualPeriod}
                />
                {lane.volume_source === 'PLANNING' && sum(activeVolume) === 0 && (
                  <p className="m-0 mt-2 text-[11px] text-amber-600 dark:text-amber-400">
                    Planning has no volume for this lane. Switch the toggle to manual volumes to type it here.
                  </p>
                )}
              </div>

              {/* Carriers */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <p className="m-0 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Carriers</p>
                    {laneRates.length > 0 && Math.abs(allocTotal - 100) > 0.01 && (
                      <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">
                        {fmtN(allocTotal)}% allocated
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button className={btn} onClick={() => addCarrier('OWNED')} disabled={busy}>
                      <Truck className="w-3.5 h-3.5" /> Own fleet
                    </button>
                    <button className={btn} onClick={() => addCarrier('CONTRACTED')} disabled={busy || !picks.contractors.length}
                            title={picks.contractors.length ? '' : 'Add a contractor under Budget Setup → Contractors first'}>
                      <Plus className="w-3.5 h-3.5" /> Contractor
                    </button>
                    <button className={btnPrimary} onClick={saveRates} disabled={busy || !Object.keys(dirtyRates).length}>
                      <Save className="w-3.5 h-3.5" /> Save
                    </button>
                  </div>
                </div>

                {laneRates.length === 0 ? (
                  <p className="m-0 py-6 text-xs text-slate-400 text-center">
                    No carrier on this lane yet — add your own fleet or a contractor.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-xs">
                      <thead>
                        <tr className="bg-slate-100 dark:bg-slate-700/60 text-left">
                          {['Carrier', 'Vehicle', 'Share %', 'Basis', 'Rate', 'Indexed to', 'Escl.', 'Posts to', 'Trips / yr', 'Cost / yr', ''].map((h) => (
                            <th key={h} className="px-2 py-2 font-bold uppercase text-[10px] tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {laneRates.map((r, idx) => (
                          <tr key={r.rate_line_id} className={`border-t border-slate-100 dark:border-slate-700/60 ${idx % 2 ? 'bg-slate-50/50 dark:bg-slate-800/40' : ''}`}>
                            <td className="px-2 py-1.5">
                              {r.carrier_type === 'OWNED' ? (
                                <span className="px-2 py-1 text-[10px] font-bold rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400">Own fleet</span>
                              ) : (
                                <SearchSelect value={r.contractor_code || ''} onChange={(v) => setRate(r.rate_line_id, { contractor_code: v })}
                                  options={picks.contractors.map((c) => ({ code: c.contractor_code, name: c.contractor_name }))}
                                  placeholder="Contractor…" />
                              )}
                            </td>
                            <td className="px-2 py-1.5">
                              <select className={field} value={r.vehicle_type_code || ''} onChange={(e) => setRate(r.rate_line_id, { vehicle_type_code: e.target.value || null })}>
                                <option value="">— none —</option>
                                {picks.vehicleTypes.map((v) => (
                                  <option key={v.vehicle_type_code} value={v.vehicle_type_code}>{v.description} ({v.capacity})</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-2 py-1.5">
                              <input className={numField} style={{ width: 62 }} type="number" min="0" max="100" value={r.allocation_pct}
                                     onChange={(e) => setRate(r.rate_line_id, { allocation_pct: Number(e.target.value) || 0 })} />
                            </td>
                            <td className="px-2 py-1.5">
                              <select className={field} value={r.rate_basis} onChange={(e) => setRate(r.rate_line_id, { rate_basis: e.target.value })}>
                                {RATE_BASES.map((b) => <option key={b.code} value={b.code}>{b.label}</option>)}
                              </select>
                            </td>
                            <td className="px-2 py-1.5">
                              <div className="flex items-center gap-1">
                                <input className={numField} style={{ width: 88 }} type="number" min="0" step="0.01"
                                       value={r.rates[0] ?? 0} onChange={(e) => setRateValue(r.rate_line_id, 1, e.target.value)} />
                                <button onClick={() => setRate(r.rate_line_id, { rate_mode: r.rate_mode === 'ANNUAL' ? 'MONTHLY' : 'ANNUAL' })}
                                        className={`px-1.5 py-1 text-[9px] font-bold uppercase rounded ${r.rate_mode === 'MONTHLY'
                                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300'
                                          : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'}`}
                                        title="One rate for the year, or twelve when a tariff steps mid-year">
                                  {r.rate_mode === 'MONTHLY' ? '12' : '1'}
                                </button>
                              </div>
                              {/* What the rate actually costs once the index is
                                  applied. Next to the typed number, not instead
                                  of it — the base is what was negotiated. */}
                              {(() => {
                                const fi = factorOf(r);
                                const fc = increaseFactorOf(r);
                                const f = fi * fc;
                                if (Math.abs(f - 1) < 0.0005) return null;
                                const eff = Number(r.rates[0] ?? 0) * f;
                                const why = [
                                  Math.abs(fi - 1) >= 0.0005 ? `index x${fi.toFixed(4)}` : null,
                                  Math.abs(fc - 1) >= 0.0005 ? `increase x${fc.toFixed(4)} (year average of the step)` : null,
                                ].filter(Boolean).join(' · ');
                                return (
                                  <span className={`block mt-0.5 text-[10px] font-mono ${f > 1 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}
                                        title={`Base x ${f.toFixed(4)} — ${why}`}>
                                    → {eff.toFixed(2)}
                                  </span>
                                );
                              })()}
                            </td>
                            {/* Index, the base it was quoted at, and how much of
                                THIS rate is that commodity. Three fields because
                                the arithmetic needs three. */}
                            <td className="px-2 py-1.5">
                              <div className="flex items-center gap-1">
                                <select className={field} style={{ width: 92 }} value={r.index_code || ''}
                                        title="Link this rate to a volatile price. Base and share are stamped from policy, then overridable here."
                                        onChange={(e) => linkRate(r, e.target.value || null)}>
                                  <option value="">— none —</option>
                                  {(picks.priceIndices || []).map((i) => (
                                    <option key={i.index_code} value={i.index_code}>{i.index_name}</option>
                                  ))}
                                </select>
                                {r.index_code && (
                                  <>
                                    <input className={numField} style={{ width: 52 }} type="number" min="0" max="100" step="1"
                                           title="What proportion of THIS rate is the commodity. 0 = a fixed-price contract, immune."
                                           value={r.index_share_pct ?? 0}
                                           onChange={(e) => setRate(r.rate_line_id, { index_share_pct: Math.min(100, Math.max(0, Number(e.target.value) || 0)) })} />
                                    <span className="text-[9px] font-bold text-slate-400">%</span>
                                    <input className={numField} style={{ width: 64 }} type="number" min="0" step="0.01"
                                           title="The price already inside this rate — what the carrier quoted at, NOT the current price."
                                           value={r.index_base_value ?? 0}
                                           onChange={(e) => setRate(r.rate_line_id, { index_base_value: Number(e.target.value) || 0 })} />
                                  </>
                                )}
                              </div>
                            </td>
                            {/* The carrier's annual increase, set once on Budget
                                Setup → Contractors. Read-only here — the % and
                                the month belong to the carrier, not the line —
                                but the line can OPT OUT, which is what a locked
                                contract needs. */}
                            <td className="px-2 py-1.5 whitespace-nowrap">
                              {r.carrier_type === 'OWNED' ? (
                                <span className="text-[10px] text-slate-400" title="Own fleet has no contractor and no escalation clause">—</span>
                              ) : !Number(r.contractor_increase_pct) || !r.contractor_increase_period ? (
                                <span className="text-[10px] text-slate-400" title="No expected increase set for this carrier — set one on Budget Setup → Contractors">none</span>
                              ) : (
                                <label className="inline-flex items-center gap-1.5 cursor-pointer"
                                       title={`${r.contractor_increase_pct}% from ${periodLabel(r.contractor_increase_period, fyStartMonth)}. Untick for a line whose price is locked for the year.`}>
                                  <input type="checkbox" checked={r.apply_contractor_increase !== false}
                                         onChange={(e) => setRate(r.rate_line_id, { apply_contractor_increase: e.target.checked })} />
                                  <span className={`text-[10px] font-mono ${r.apply_contractor_increase === false ? 'text-slate-400 line-through' : 'text-amber-600 dark:text-amber-400'}`}>
                                    +{Number(r.contractor_increase_pct).toFixed(1)}%
                                    <span className="block text-[9px] text-slate-400">{periodLabel(r.contractor_increase_period, fyStartMonth)}</span>
                                  </span>
                                </label>
                              )}
                            </td>
                            {/* Read-only: the posting is decided once, centrally,
                                on Budget Setup → Inter-Depot Posting. Shown here
                                so the manager can see where his line lands
                                without being able to redirect it. */}
                            <td className="px-2 py-1.5 whitespace-nowrap">
                              {r.posting?.account_code || r.posting?.cost_center_code ? (
                                <>
                                  <span className="font-mono text-slate-600 dark:text-slate-300">{r.posting.account_code || '—'}</span>
                                  <span className="block text-[10px] text-slate-400">{r.posting.cost_center_code || 'no cost centre'}</span>
                                </>
                              ) : (
                                <span className="text-[10px] text-amber-600 dark:text-amber-400" title="No rule covers this lane — set one under Budget Setup → Inter-Depot Posting">
                                  not set
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono text-slate-600 dark:text-slate-300">{fmtN(sum(r.cost?.trips))}</td>
                            <td className="px-2 py-1.5 text-right font-mono font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap">{fmtR(sum(r.cost?.amount))}</td>
                            <td className="px-2 py-1.5">
                              <button onClick={() => run(() => interDepotService.deleteRateLine(r.rate_line_id), 'Carrier removed.')}
                                      className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10" title="Remove carrier">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/70">
                          <td colSpan={9} className="px-2 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                            Lane cost / year
                          </td>
                          <td className="px-2 py-2 text-right font-mono text-sm font-bold text-slate-800 dark:text-slate-100 whitespace-nowrap">{fmtR(laneCost)}</td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                <p className="m-0 mt-2 text-[11px] text-slate-400">
                  Share is this carrier&rsquo;s slice of the lane volume — the lines should sum to 100%.
                  Own-fleet lines carry the variable cost of movement only; lease, insurance and licensing belong on
                  Warehousing → Fixed Costs. Where a line posts is set once on Budget Setup → Inter-Depot Posting.
                </p>
                <p className="m-0 mt-1.5 text-[11px] text-slate-400">
                  <strong>Indexed to</strong> links a rate to a volatile price — diesel — so one entry on Budget Setup → Price Indices moves it.
                  The <strong>%</strong> is how much of <em>this</em> rate is that commodity (per-km line-haul is around 40, a per-trip charge
                  a little less, a per-pallet charge mostly handling); <strong>0 means immune</strong>, which is what a genuinely fixed-price contract is.
                  The last box is the price the carrier <em>quoted at</em>, not today&rsquo;s — a line quoted after a fuel rise must not be charged for it twice.
                  The typed rate is never overwritten; the arrow underneath is what it costs once the index is applied.
                </p>
                <p className="m-0 mt-1.5 text-[11px] text-slate-400">
                  <strong>Escl.</strong> is the carrier&rsquo;s expected annual increase, set once on Budget Setup → Contractors and applied as a
                  <em> step</em> from the month shown — untick it for a line whose price is locked for the year. It stacks on the index
                  (<span className="font-mono">base × diesel × increase</span>), so if a carrier&rsquo;s annual rise is how they recover fuel
                  rather than a separate adjustment clause, give that line a fuel share of <strong>0</strong> or diesel is counted twice.
                </p>
              </div>

              <CostByPeriod
                laneRates={laneRates}
                allRates={rates}
                picks={picks}
                metric={costMetric}
                onMetric={setCostMetric}
                fyStartMonth={fyStartMonth}
                capacityUom={capacityUom}
                onRateEdit={editRate}
                onEscalate={applyEscalation}
                busy={busy}
              />

              <PostingSummary
                entries={rates.filter((r) => r.posting?.account_code).map((r) => {
                  const l = lanes.find((x) => x.lane_id === r.lane_id);
                  return {
                    site: l?.origin_site_code || '',
                    gl: r.posting.account_code,
                    costCentre: r.posting.cost_center_code || '',
                    values: r.cost?.amount || [],
                  };
                })}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ---- Lane volume (grid) -----------------------------------------------------
// One editable row when the lane is on manual volumes, plus a read-only
// planning row for reference whenever planning has something to say. Seeing
// both is the point: a manager overriding the plan should be able to see what
// he is overriding without leaving the page.
//
// On Glide rather than an input strip so twelve months paste in from Excel in
// one go — which is how a distribution manager actually holds this.
const VolumeGrid = ({ lane, manual, planning, fyStartMonth, onEdit }) => {
  const isManual = lane.volume_source === 'MANUAL';
  const hasPlanning = sum(planning) > 0;
  // The reference row only earns its space when it is not the row in force.
  const showPlanning = hasPlanning && isManual;

  // Periods drill down — Full Year → quarters → months, same as OPEX entry.
  // Lane volumes are quantities, so a group column SUMS its months.
  const drill = usePeriodDrilldown(PERIODS);

  const columns = useMemo(() => ([
    { id: 'label', title: 'Source', width: 168 },
    ...periodColumns(drill, fyStartMonth, { monthWidth: 78, groupWidth: 112 }),
    { id: 'year', title: 'Year', width: 104 },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [fyStartMonth, drill.visibleCols, drill.yearExpanded, drill.expandedQuarters]);

  const rows = useMemo(() => {
    const out = [{
      label: isManual ? 'Lane volume' : 'From planning',
      values: isManual ? manual : planning,
      editable: isManual,
    }];
    if (showPlanning) out.push({ label: 'Planning (reference)', values: planning, editable: false });
    return out;
  }, [isManual, manual, planning, showPlanning]);

  const getCellContent = useCallback(([col, row]) => {
    const r = rows[row];
    if (!r) return gcell.text('');
    if (col === 0) return gcell.text(r.label, { readonly: true, theme: r.editable ? T_LABEL : T_READONLY });
    if (col === columns.length - 1) {
      const y = sum(r.values);
      return gcell.number(y, { readonly: true, display: fmtN(y), theme: T_TOTAL });
    }
    // By ID, not offset — the period columns fold into quarters now.
    const ps = periodsOfCol(columns, columns[col]?.id);
    if (!ps) return gcell.text('', { readonly: true });
    const v = aggPeriods(ps, (p) => r.values[p - 1]);
    const agg = ps.length > 1;
    return gcell.number(v, {
      readonly: !r.editable || agg,
      display: fmtN(v),
      theme: (r.editable && !agg) ? undefined : T_READONLY,
    });
  }, [rows, columns]);

  const onCellEdited = useCallback(([col, row], newValue) => {
    if (!rows[row]?.editable) return;
    const ps = periodsOfCol(columns, columns[col]?.id);
    if (ps?.length !== 1) return; // months only
    const col_ = ps[0];
    const n = Number(newValue?.data);
    if (Number.isNaN(n) || n < 0) return;
    onEdit(col_, n);
  }, [rows, columns, onEdit]);

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
      <MantleDataGrid
        columns={columns}
        rowCount={rows.length}
        getCellContent={getCellContent}
        onCellEdited={onCellEdited}
        onHeaderClicked={(col) => periodHeaderToggle(drill, columns[col]?.id)}
        freezeColumns={1}
        gridId="interdepot-volume"
        fitHeight
        fitHeightPadding={6}
        hint={isManual
          ? 'Click Full Year → quarters → months to drill in. Paste twelve months straight from Excel, or Ctrl+R to fill right.'
          : 'Click Full Year → quarters → months to drill in. Derived from the planning lane — switch the toggle above to type volumes here.'}
      />
    </div>
  );
};

// ---- Cost by period ---------------------------------------------------------
// The lane's money, month by month. A transport budget is a cash-flow question
// as much as a total one — a lane that costs the same for the year but lands
// three-quarters of it in peak is a different problem to fund.
//
// RATE IS EDITABLE HERE, and that is the point of the monthly view: a tariff or
// fuel increase lands in a month, and this is where it gets entered. Typing a
// month flips that carrier to monthly rates automatically — leaving the user to
// find a mode toggle first is how an edit gets silently spread back across the
// whole year on save.
//
// Cost, trips and volume are DERIVED and read-only: change the share, the rate
// or the volume and they follow. They share this grid because they explain the
// money — a cost that jumps in one month is a rate step if trips are flat and a
// volume spike if they are not, and nobody should leave the page to tell which.
const METRICS = [
  { code: 'amount', label: 'Cost', money: true, editable: false },
  { code: 'rate', label: 'Rate', money: true, editable: true },
  { code: 'trips', label: 'Trips', money: false, editable: false },
  { code: 'carrier_qty', label: 'Volume', money: false, editable: false },
];

const CostByPeriod = ({
  laneRates, allRates, picks, metric, onMetric,
  fyStartMonth, capacityUom, onRateEdit, onEscalate, busy,
}) => {
  const spec = METRICS.find((m) => m.code === metric) || METRICS[0];
  const [pct, setPct] = useState('5');
  const [fromPeriod, setFromPeriod] = useState(7);

  const isRate = metric === 'rate';

  // The rate lives on the line itself; everything else comes off the derived
  // cost payload.
  const seriesOf = useCallback(
    (r) => (isRate ? (r.rates || Array(12).fill(0)) : (r.cost?.[metric] || Array(12).fill(0))),
    [isRate, metric]
  );

  const carrierName = useCallback((r) => (r.carrier_type === 'OWNED'
    ? 'Own fleet'
    : picks.contractors.find((c) => c.contractor_code === r.contractor_code)?.contractor_name
      || r.contractor_code || 'Contractor'),
  [picks.contractors]);

  const show = useCallback((n) => (spec.money ? fmtR(n) : fmtN(n)), [spec.money]);

  const columns = useMemo(() => ([
    { id: 'carrier', title: 'Carrier', width: 190 },
    ...PERIODS.map((p) => ({ id: `P${p}`, title: periodLabel(p, fyStartMonth), width: 86 })),
    // A rate is a price, not a quantity: twelve of them added together mean
    // nothing, so the last column averages instead of totalling.
    { id: 'year', title: isRate ? 'Avg' : 'Year', width: 112 },
  ]), [fyStartMonth, isRate]);

  const laneTotals = useMemo(
    () => PERIODS.map((_, i) => laneRates.reduce((a, r) => a + (seriesOf(r)[i] || 0), 0)),
    [laneRates, seriesOf]
  );
  const allTotals = useMemo(
    () => PERIODS.map((_, i) => allRates.reduce(
      (a, r) => a + ((isRate ? (r.rates || []) : (r.cost?.[metric] || []))[i] || 0), 0
    )),
    [allRates, isRate, metric]
  );

  const getCellContent = useCallback(([col, row]) => {
    // Two footer rows: this lane, then the whole network.
    if (row >= laneRates.length) {
      const isAll = row === laneRates.length + 1;
      const totals = isAll ? allTotals : laneTotals;
      const theme = isAll ? T_SUBTOTAL : T_TOTAL;
      if (col === 0) return gcell.text(isAll ? 'All lanes' : 'This lane', { readonly: true, theme });
      if (col === columns.length - 1) {
        const y = isRate ? sum(totals) / 12 : sum(totals);
        return gcell.number(y, { readonly: true, display: show(y), theme });
      }
      return gcell.number(totals[col - 1], { readonly: true, display: show(totals[col - 1]), theme });
    }

    const r = laneRates[row];
    if (!r) return gcell.text('');
    if (col === 0) return gcell.text(`${carrierName(r)}  ${fmtN(r.allocation_pct)}%`, { readonly: true, theme: T_LABEL });

    const s = seriesOf(r);
    if (col === columns.length - 1) {
      const y = isRate ? sum(s) / 12 : sum(s);
      return gcell.number(y, { readonly: true, display: show(y), theme: T_TOTAL });
    }
    const v = s[col - 1] || 0;
    return gcell.number(v, {
      readonly: !spec.editable,
      display: show(v),
      theme: spec.editable ? undefined : T_READONLY,
    });
  }, [laneRates, allTotals, laneTotals, columns.length, isRate, show, carrierName, seriesOf, spec.editable]);

  const onCellEdited = useCallback(([col, row], newValue) => {
    if (!spec.editable || row >= laneRates.length) return;
    if (col < 1 || col > 12) return;
    const n = Number(newValue?.data);
    if (Number.isNaN(n) || n < 0) return;
    onRateEdit(laneRates[row].rate_line_id, col, n);
  }, [spec.editable, laneRates, onRateEdit]);

  if (!laneRates.length) return null;

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <p className="m-0 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          {spec.label} by period{metric === 'carrier_qty' ? ` (${capacityUom.toLowerCase()})` : ''}
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          {/* "5% from July" — the actual shape of a tariff increase, applied to
              every carrier on the lane at once. */}
          {isRate && (
            <div className="flex items-center gap-1.5 pr-2 mr-1 border-r border-slate-200 dark:border-slate-600">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Increase</span>
              <input className={numField} style={{ width: 62 }} type="number" step="0.5"
                     value={pct} onChange={(e) => setPct(e.target.value)} />
              <span className="text-[11px] text-slate-400">% from</span>
              <select className={field} value={fromPeriod} onChange={(e) => setFromPeriod(Number(e.target.value))}>
                {PERIODS.map((p) => <option key={p} value={p}>{periodLabel(p, fyStartMonth)}</option>)}
              </select>
              <button className={btn} onClick={() => onEscalate(pct, fromPeriod)} disabled={busy}>Apply</button>
            </div>
          )}
          <div className="flex items-center rounded-md border border-slate-300 dark:border-slate-600 overflow-hidden">
            {METRICS.map((m) => (
              <button key={m.code} onClick={() => onMetric(m.code)}
                className={`px-2.5 py-1 text-[11px] font-semibold transition-colors ${metric === m.code
                  ? 'bg-slate-700 text-white dark:bg-slate-600'
                  : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        <MantleDataGrid
          columns={columns}
          rowCount={laneRates.length + 2}
          getCellContent={getCellContent}
          onCellEdited={onCellEdited}
          freezeColumns={1}
          gridId={`interdepot-period-${metric}`}
          fitHeight
          fitHeightPadding={6}
          hint={spec.editable
            ? 'Type a month to set that period’s rate — the line switches to monthly rates automatically. Paste from Excel, or Ctrl+R to fill right. Remember to Save above.'
            : 'Derived from share × volume × rate. Switch to Rate to enter an increase.'}
        />
      </div>

      <p className="m-0 mt-2 text-[11px] text-slate-400">
        {isRate
          ? 'The last column is an average, not a total — twelve prices added together mean nothing. Rates save with the carrier lines above.'
          : 'Nothing here is typed. A flat monthly rate still gives an uneven cost profile, because trips follow the volume plan.'}
      </p>
    </div>
  );
};

// ---- Network view: legend ---------------------------------------------------
const NetworkLegend = () => (
  <div className="text-xs text-slate-500 dark:text-slate-400 space-y-3">
    <p className="m-0 font-bold uppercase tracking-wider text-slate-400">Lanes</p>
    <div className="space-y-1.5">
      <p className="m-0"><span className="inline-block w-6 border-t-2 border-emerald-600 align-middle mr-1.5" /> Priced, carriers add to 100%</p>
      <p className="m-0"><span className="inline-block w-6 border-t-2 border-red-600 align-middle mr-1.5" /> Carriers do not add to 100%</p>
      <p className="m-0"><span className="inline-block w-6 border-t-2 border-dashed border-slate-300 align-middle mr-1.5" /> No carrier — costs nothing, and should not</p>
      <p className="m-0">The chevron mid-lane points the way the stock travels.</p>
    </div>
    <p className="m-0 pt-2 border-t border-slate-100 dark:border-slate-700">
      Click a lane to price it here, or drag a site to untangle the map.
      <strong className="block mt-1 text-slate-600 dark:text-slate-300">Why a map</strong>
      A lane nobody carries costs zero and looks perfectly fine in a list. On a graph it is a dashed
      line going somewhere, which is much harder to walk past.
    </p>
    <p className="m-0 text-slate-400">
      The layout is not saved — this map is a way in to a lane, not a document. Planning &rsaquo; Network
      is the one that keeps its arrangement.
    </p>
  </div>
);

// ---- Network view: the lane popup ------------------------------------------
// Everything a lane needs priced, in the panel: who carries it (own fleet or a
// contractor), on what basis, at what share and rate. The full rate card next
// door adds the index link, the posting and the monthly grid — this is the fast
// path into a lane, not a second implementation of it.
const NetworkLaneInspector = ({ edge, rates, picks, busy, onEdit, onAddCarrier, onRemove, onSave, onOpenFull, dirty }) => {
  if (!edge) return null;
  const share = rates.reduce((a, r) => a + (Number(r.allocation_pct) || 0), 0);
  const cost = rates.reduce((a, r) => a + sum(r.cost?.amount), 0);
  const shareBad = rates.length > 0 && Math.round(share) !== 100;

  return (
    <div className="space-y-3">
      <div>
        <p className="m-0 text-[10px] font-bold uppercase tracking-wider text-slate-400">Lane</p>
        <p className="m-0 mt-1 text-sm font-bold text-slate-800 dark:text-slate-100">{edge.lane.origin_site_code}</p>
        <p className="m-0 text-base leading-none text-slate-400">↓</p>
        <p className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">{edge.lane.dest_site_code}</p>
        <p className="m-0 mt-1 text-[11px] text-slate-400">
          {fmtN(edge.lane.distance_km)} km one way · return factor {edge.lane.return_factor}
        </p>
      </div>

      <div className={`px-2 py-1.5 rounded-md text-[11px] ${shareBad
        ? 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 font-semibold'
        : 'bg-slate-50 dark:bg-slate-900/50 text-slate-500 dark:text-slate-400'}`}>
        Carrier share {fmtN(share)}% · {fmtR(cost)} / yr
        {shareBad && <span className="block mt-0.5 font-normal">The budget is out by exactly that gap.</span>}
      </div>

      {rates.length === 0 && (
        <p className="m-0 rounded-md bg-amber-50 dark:bg-amber-500/10 px-2 py-2 text-[11px] text-amber-700 dark:text-amber-300">
          Nothing carries this lane yet, so it budgets at zero.
        </p>
      )}

      {rates.map((r) => (
        <div key={r.rate_line_id} className="rounded-lg border border-slate-200 dark:border-slate-700 p-2 space-y-1.5">
          <div className="flex items-center gap-1.5">
            {/* Own fleet or contracted, chosen first — it decides whether the
                next control is a contractor picker or nothing at all. */}
            <select className={field} style={{ flex: 1 }} value={r.carrier_type}
                    onChange={(e) => onEdit(r.rate_line_id, {
                      carrier_type: e.target.value,
                      contractor_code: e.target.value === 'OWNED' ? null : (r.contractor_code || picks.contractors[0]?.contractor_code),
                    })}>
              <option value="OWNED">Own fleet</option>
              <option value="CONTRACTED">Contracted</option>
            </select>
            <button onClick={() => onRemove(r.rate_line_id)} disabled={busy}
                    className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>

          {r.carrier_type === 'CONTRACTED' && (
            <SearchSelect value={r.contractor_code || ''} onChange={(v) => onEdit(r.rate_line_id, { contractor_code: v })}
              options={picks.contractors.map((c) => ({ code: c.contractor_code, name: c.contractor_name }))}
              placeholder="Contractor…" />
          )}

          <select className={`${field} w-full`} value={r.vehicle_type_code || ''}
                  onChange={(e) => onEdit(r.rate_line_id, { vehicle_type_code: e.target.value || null })}>
            <option value="">— vehicle —</option>
            {picks.vehicleTypes.map((v) => (
              <option key={v.vehicle_type_code} value={v.vehicle_type_code}>{v.description} ({v.capacity})</option>
            ))}
          </select>

          <div className="flex items-center gap-1.5">
            <select className={field} style={{ flex: 1 }} value={r.rate_basis}
                    onChange={(e) => onEdit(r.rate_line_id, { rate_basis: e.target.value })}>
              {RATE_BASES.map((b) => <option key={b.code} value={b.code}>{b.label}</option>)}
            </select>
            {/* One annual rate here on purpose. A mid-year step is a twelve-box
                decision and belongs on the rate card, where the twelve boxes are. */}
            <input className={numField} style={{ width: 78 }} type="number" step="0.01"
                   value={r.rates[0] ?? 0}
                   onChange={(e) => {
                     const v = Number(e.target.value) || 0;
                     onEdit(r.rate_line_id, { rates: Array(12).fill(v), rate_mode: 'ANNUAL' });
                   }} />
          </div>

          <div className="flex items-center gap-1">
            <input className={numField} style={{ width: 58 }} type="number" min="0" max="100"
                   value={r.allocation_pct}
                   onChange={(e) => onEdit(r.rate_line_id, { allocation_pct: Number(e.target.value) || 0 })} />
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">% of lane</span>
            <span className="ml-auto font-mono text-[11px] text-slate-600 dark:text-slate-300">{fmtR(sum(r.cost?.amount))}</span>
          </div>
        </div>
      ))}

      <div className="flex items-center gap-1.5">
        <button className={btn} onClick={() => onAddCarrier('OWNED')} disabled={busy}>
          <Plus className="w-3.5 h-3.5" /> Own
        </button>
        <button className={btn} onClick={() => onAddCarrier('CONTRACTED')} disabled={busy || !picks.contractors.length}>
          <Plus className="w-3.5 h-3.5" /> Contractor
        </button>
      </div>

      <div className="pt-2 border-t border-slate-100 dark:border-slate-700 space-y-1.5">
        <button className={`${btnPrimary} w-full justify-center`} onClick={onSave} disabled={busy || !dirty}>
          <Save className="w-3.5 h-3.5" /> Save
        </button>
        <button className={`${btn} w-full justify-center`} onClick={onOpenFull}>
          Open in Lanes &amp; Rates
        </button>
        <p className="m-0 text-[10px] text-slate-400">
          Diesel linking, the contractor increase, posting and month-by-month rates live on the full rate card.
        </p>
      </div>
    </div>
  );
};
